'use client';

import { normalizeTrackingValue, trackEvent, EventClickArea } from '@tuic/tracking';
import { FC, FocusEvent, PropsWithChildren, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twJoin } from '@tuic/tailwind-config';
import { IconButton } from '../../../core-components';
const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"]):not([data-focus-sentinel])';
type DialogPersistentProps = PropsWithChildren<{
  /**
   * The controlled open state of the dialog. Must be used in conjunction with onOpenChange.
   */
  open?: boolean;

  /**
   * Callback fired when the dialog's open state changes.
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Name of the Dialog used for tracking
   */
  trackingName?: string;

  /**
   * The title displayed at the top of the dialog.
   */
  title?: string;

  /**
   * Used to display the Dialog in a fullscreen mode across the entire viewport.
   */
  fullscreen?: boolean;

  /**
   * Used to display the Dialog in a windowed mode on smaller viewport.
   */
  mobileWindowed?: boolean;

  /**
   * Used to display the dialog at maximum height, regardless of its children's height.
   */
  expanded?: boolean;

  /**
   * Used to display a medium or large width dialog on desktop. Defaults to `'md'`.
   */
  desktopSize?: 'md' | 'lg' | 'xl';

  /**
   * Specifies the container in which the dialog will render. If not provided, the dialog will render in the document body.
   */
  container?: HTMLElement;

  /**
   * Used to remove the default padding from the dialog content.
   */
  disableSpacing?: boolean;

  /**
   * The CTA-Link on the left side
   */
  ctaLink?: ReactNode;

  /**
   * The CTA-Button on the right side
   */
  ctaButton?: ReactNode;

  /**
   * The hero content of the dialog, displayed at the top of the dialog.
   */
  heroContent?: ReactNode;

  /**
   * The footer content of the dialog. Sticks to the bottom of the dialog.
   */
  footerContent?: ReactNode;

  /**
   * Limits the height of the content to the dialog height (useful when a fixed bottom bar is needed)
   */
  heightLimited?: boolean;

  /**
   * Used to fix the size to 90vh on mobile.
   */
  nearlyFullScreen?: boolean;

  /**
   * Callback to call on click on back button.
   * When provided, a back button will be displayed in the dialog header.
   */
  onBack?: () => void;

  /**
   * Displays the close button inside the dialog header (default = true)
   */
  showCloseButton?: boolean;

  /**
   * CSS classes on dialog body.
   */
  bodyClassName?: string;

  /**
   * Event handler called when the escape key is down.
   */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;

  /**
   * Event handler called when a pointer event occurs outside the bounds of the component.
   */
  onPointerDownOutside?: (event: MouseEvent) => void;
}>;

/**
 * The DialogPersistent component is a version of Dialog that stays in the DOM even when closed.
 * It's designed to be opened programmatically without a trigger.
 *
 * ### Developer Notes
 *
 * * This dialog is always present in the DOM, but visibility is controlled by the `open` prop.
 * * This dialog is designed to be opened programmatically, so it doesn't include a trigger.
 *
 * ```tsx
 * // controlled:
 * // use the `open` and `onOpenChange` props to control
 * // the appearance state of the dialog from outside.
 *
 * import { useState } from 'react';
 * import Button from '@tuic/web-compositions/core/Button';
 * import { DialogPersistent } from '@tuic/web-compositions/shared/DialogPersistent';
 *
 * const [open, setOpen] = useState(false);
 *
 * // ...
 *
 * <Button label="Open Dialog" onClick={() => setOpen(true)}/>
 *
 * <DialogPersistent
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Title of the Dialog"
 * >
 *   <div>
 *     Content of the dialog.
 *   </div>
 * </DialogPersistent>
 * ```
 */

const DialogPersistent: FC<DialogPersistentProps> = ({
  open = false,
  onOpenChange,
  trackingName,
  title,
  fullscreen = false,
  mobileWindowed = false,
  desktopSize = 'md',
  expanded = false,
  container,
  children,
  disableSpacing = false,
  heightLimited = false,
  nearlyFullScreen = false,
  ctaLink,
  ctaButton,
  heroContent,
  footerContent,
  onBack,
  showCloseButton = true,
  bodyClassName,
  onEscapeKeyDown,
  onPointerDownOutside
}) => {
  const [mounted, setMounted] = useState(false);
  const openedAtLeastOnce = useRef<boolean>(open);
  const contentRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const previousActiveRef = useRef<HTMLElement | null>(null);
  const handleKeyDown = useCallback((ev: KeyboardEvent) => {
    if (ev.key !== 'Escape') {
      return;
    }
    onEscapeKeyDown?.(ev);
    onOpenChange?.(false);
  }, [onEscapeKeyDown, onOpenChange]);
  const handlePointerDown = useCallback((ev: MouseEvent) => {
    const dialogContent = contentRef.current;
    const dialogOverlay = overlayRef.current;

    // If click is inside dialog content, don't close
    if (!dialogContent || dialogContent.contains(ev.target as Node)) {
      return;
    }

    // If a custom handler is provided, call it first
    // This allows consumers to handle the event before our logic
    if (typeof onPointerDownOutside === 'function') {
      onPointerDownOutside(ev);
      // Custom handler is responsible for closing, so we return
      return;
    }

    // Check if clicked element is visually above the dialog overlay
    // This prevents closing when clicking on toasts, tooltips, etc.
    if (dialogOverlay) {
      const elementsAtPoint = document.elementsFromPoint(ev.clientX, ev.clientY);
      const overlayIndex = elementsAtPoint.indexOf(dialogOverlay);

      // Check if any element above the overlay was clicked
      // Elements before overlayIndex are visually on top
      if (overlayIndex > -1) {
        for (let i = 0; i < overlayIndex; i++) {
          if (elementsAtPoint[i].contains(ev.target as Node)) {
            // Click was on element above dialog - don't close
            return;
          }
        }
      }
    }

    // Default behavior: close the dialog
    onOpenChange?.(false);
  }, [onPointerDownOutside, onOpenChange]);

  // Handle escape key press
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Handle clicks outside the dialog
  useEffect(() => {
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [handlePointerDown]);
  useEffect(() => {
    if (open) {
      openedAtLeastOnce.current = true;
    }
    const shouldTrack = open || openedAtLeastOnce.current;
    if (!trackingName || !shouldTrack) {
      return;
    }
    trackEvent({
      event_name: open ? 'layer-open-user' : 'layer-close',
      layer_name: normalizeTrackingValue(trackingName)
    });
  }, [open, trackingName]);
  const handleSentinelFocus = useCallback((e: FocusEvent<HTMLElement>) => {
    const focusables = overlayRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (!focusables?.length) return;
    const target = e.currentTarget.dataset.focusSentinel === 'leading' ? focusables[focusables.length - 1] : focusables[0];
    target.focus();
  }, []);

  // Cleanup order matters: capture opener → mark inert → move focus in;
  // on close unwind in reverse so the opener is focusable again before restore.
  useEffect(() => {
    if (!open) return;
    const overlay = overlayRef.current;
    if (!overlay) return;

    // Capture once per open session: guard against re-runs from `container` changing mid-open.
    if (!previousActiveRef.current) {
      const activeEl = document.activeElement;
      previousActiveRef.current = activeEl instanceof HTMLElement ? activeEl : null;
    }
    const root = container ?? document.body;
    const markedInert = Array.from(root.children).filter((el): el is HTMLElement => el instanceof HTMLElement && !el.contains(overlay) && !el.hasAttribute('inert'));
    markedInert.forEach(el => el.setAttribute('inert', ''));
    const focusables = overlay.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const firstElement = focusables[0];
    const lastElement = focusables[focusables.length - 1];
    firstElement?.focus();
    const handleTabKeyPress = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === firstElement) {
        lastElement?.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        firstElement?.focus();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleTabKeyPress);
    return () => {
      window.removeEventListener('keydown', handleTabKeyPress);
      markedInert.forEach(el => el.removeAttribute('inert'));
      previousActiveRef.current?.focus();
      previousActiveRef.current = null;
    };
  }, [open, container]);
  const dialogContent = <div ref={overlayRef} className={twJoin('fixed inset-0 z-[1200] flex backdrop-blur-sm', 'data-[state=open]:animate-in data-[state=closed]:animate-out', 'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', 'data-[state=closed]:invisible data-[state=open]:flex', 'data-[state=closed]:pointer-events-none',
  // 'firefox:data-[state=closed]:animate-none firefox:data-[state=open]:animate-none',
  'firefox:data-[state=closed]:hidden', !fullscreen && twJoin('items-end bg-black/60 backdrop-blur-sm md:items-center md:justify-center md:p-8', 'data-[state=open]:duration-300', 'data-[state=closed]:duration-500'), mobileWindowed && 'items-center justify-center p-4')} data-state={open ? 'open' : 'closed'}>
      <div ref={contentRef} role="dialog" aria-modal="true" aria-labelledby="dialog-title" data-track-click-area={EventClickArea.Dialog} data-track-dialog={trackingName ? normalizeTrackingValue(trackingName) : title} className={twJoin('relative z-[1000] flex w-full overflow-hidden bg-white', 'duration-200', 'data-[state=open]:animate-in data-[state=open]:fade-in-0 md:data-[state=open]:zoom-in-95', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 md:data-[state=closed]:zoom-out-95', 'data-[state=closed]:invisible data-[state=open]:flex', 'data-[state=closed]:pointer-events-none',
    // 'firefox:data-[state=closed]:animate-none firefox:data-[state=open]:animate-none',
    'firefox:data-[state=closed]:hidden', fullscreen && 'fixed inset-0 h-full w-full', !fullscreen && twJoin('max-h-[calc(100%_-_theme(space.8))] rounded-t-md shadow-200 md:max-h-full md:rounded-md', 'data-[state=open]:duration-300 data-[state=open]:slide-in-from-bottom-1/2 md:data-[state=open]:slide-in-from-bottom-0', 'data-[state=closed]:duration-500 data-[state=closed]:slide-out-to-bottom-1/2 md:data-[state=closed]:slide-out-to-bottom-0'), !fullscreen && desktopSize === 'md' && 'md:max-w-[640px]', !fullscreen && desktopSize === 'lg' && 'md:max-w-[996px]', !fullscreen && desktopSize === 'xl' && 'md:max-w-[1600px]', nearlyFullScreen && 'h-[90vh]', expanded && 'h-full', mobileWindowed && 'rounded-md rounded-t-md')} data-state={open ? 'open' : 'closed'}>
        <button type="button" className="size-0" data-focus-sentinel="leading" aria-hidden="true" onFocus={handleSentinelFocus} />
        <div className={twJoin('absolute left-0 top-0 flex h-12 w-full flex-col items-center justify-center bg-white px-14 py-4 text-center shadow-sm', !fullscreen && 'rounded-t-md')}>
          {onBack && <div className="absolute left-4 top-0 flex h-12 items-center justify-center">
              <IconButton iconName="arrow-left" aria-label={title ? `Go back in ${title} dialog` : 'Go back'} variant="secondary" size="sm" onClick={onBack} />
            </div>}
          <h2 id="dialog-title" className="relative max-w-full overflow-ellipsis text-nowrap text-base font-semibold leading-none tracking-tight text-marine-high-emphasis">
            {title}
          </h2>
          {showCloseButton && <div className="absolute right-4 top-0 flex h-12 items-center justify-center">
              <IconButton iconName="cancel" variant="secondary" size="sm" aria-label={title ? `Close ${title} dialog` : 'Close dialog'} onClick={() => onOpenChange?.(false)} />
            </div>}
        </div>
        <div className={twJoin('mt-12 flex max-h-[calc(100%_-_theme(space.12))] w-full flex-col overflow-y-auto', bodyClassName)}>
          {heroContent}
          <div className={twJoin('flex grow flex-col gap-8', !disableSpacing && 'p-6 md:p-10', heightLimited && 'max-h-full')}>
            {children}
            {(!!ctaLink || !!ctaButton) && <div className="flex flex-col justify-between gap-4 sm:flex-row">
                <div>
                  {!!ctaLink && <div className="text-base text-marine-high-emphasis underline">
                      {ctaLink}
                    </div>}
                </div>
                <div className="flex gap-6">{ctaButton}</div>
              </div>}
          </div>

          {footerContent && <div className={twJoin('sticky -bottom-0 left-0 right-0 bg-black/10', 'pb-6 pt-4 md:pb-4', !disableSpacing && 'px-6 md:px-10', 'border-t border-marine-40 bg-white')}>
              {footerContent}
            </div>}
        </div>
        <button type="button" className="size-0" data-focus-sentinel="trailing" aria-hidden="true" onFocus={handleSentinelFocus} />
      </div>
    </div>;
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  if (!mounted) {
    return null;
  }
  return container ? createPortal(dialogContent, container) : createPortal(dialogContent, document.body);
};

// export { DialogPersistent };

export { DialogPersistent, type DialogPersistentProps };